import React from 'react'
import SimpleBar from 'simplebar-react'
import { ChevronDown, XCircle, XClose } from '@untitled-ui/icons-react'
import {
  MyButton,
  MyDoubleCard,
  WhatsApp,
  MyAvatar,
  MyPopper,
  MyContextMenu,
} from '@interstellar-component'
import { format } from 'date-fns'
import AuthImage from '@src/components/AuthImage'
import MySLAStatusChip from './MySLAStatusChip'
import MyTextField from '../../../../interstellar-component/components/TextField/MyTextField'
import MyTextArea from '../../../../interstellar-component/components/TextField/MyTextArea'

function formatDate(dateStr) {
  if (!dateStr) return '-'
  try {
    return format(new Date(dateStr), 'd MMM yyyy')
  } catch {
    return dateStr
  }
}

function formatDateTime(dateStr) {
  if (!dateStr) return '-'
  try {
    return format(new Date(dateStr), 'd MMM yyyy • HH:mm')
  } catch {
    return dateStr
  }
}

const ACTOR_TYPE_LABEL = {
  user: 'Admin',
  system: 'System',
  vendor: 'CLIK',
  clik: 'CLIK',
  candidate: 'Candidate',
  employee: 'Employee',
}

/**
 * Inner panel rendering the "Form submission" view for the currently
 * selected history row. Designed to sit as the LEFT column inside the
 * Sent-to-CLIK slider (alongside the main detail on the right), so it
 * provides only its own sticky header + independently-scrolling body —
 * no slider chrome / close button (the parent owns those).
 */
export default function FormSubmissionPanel({
  historyRow,
  identityFields,
  whatsappNumber,
  ktpPhotoUrl,
  selfiePhotoUrl,
  score,
  loading,
  onCancelRequest,
  onClose,
  showActionButton = true,
}) {
  const row = historyRow || {}
  const submissionDate = formatDate(row.date)
  const statusLabel = row.status_label || row.status || 'Form submission'

  const isRejection =
    row.event_type === 'verification_rejected' ||
    /reject/i.test(row.status_label || '')

  return (
    <div className="relative flex h-full w-[375px] flex-col overflow-hidden border-r border-gray-200">
      {/* Per-column close X (Figma shows each column has its own X — both
          close the entire slider). */}
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 z-10 flex h-10 w-10 items-center justify-center rounded-lg border-none p-2 text-gray-400 shadow-none outline-none hover:bg-gray-50 active:bg-gray-100"
        >
          <XClose size={20} stroke="currentColor" />
        </button>
      )}

      {/* Sticky header */}
      <header className="border-b border-gray-100 px-6 pb-4 pt-6 pr-16">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <h2 className="text-xl font-semibold text-gray-900">Form submission</h2>
            <p className="text-sm text-gray-500">{submissionDate}</p>
          </div>
          <div className="flex flex-col gap-4">
            <div className="w-max">
              <MySLAStatusChip status={statusLabel} />
            </div>

            {showActionButton && (
              <MyPopper
                placement="bottom-start"
                target={(open, show) => (
                  <MyButton
                    onClick={show}
                    color="primary"
                    variant="outlined"
                    size="md"
                    customClassname="gap-2 w-max"
                  >
                    Action
                    <ChevronDown
                      size={20}
                      className={`text-brand/700 transition-transform ${open ? 'rotate-180' : ''}`}
                    />
                  </MyButton>
                )}
              >
                {(_open, _anchorEl, _show, close) => (
                  <MyContextMenu
                    menuButtonGroups={[
                      [
                        {
                          icon: <XCircle />,
                          label: 'Cancel request',
                          color: '#b42318',
                          onClick: () => {
                            onCancelRequest?.()
                            close()
                          },
                        },
                      ],
                    ]}
                  />
                )}
              </MyPopper>
            )}
          </div>
        </div>
      </header>

      {/* Scrollable body */}
      <section className="flex-1 overflow-hidden bg-gray-50/30">
        <SimpleBar forceVisible="y" style={{ maxHeight: '100%' }}>
          <div className="flex flex-col gap-6 px-6 py-6">
            {loading && !historyRow && (
              <div className="flex items-center justify-center py-8">
                <p className="text-sm text-gray-500">Loading...</p>
              </div>
            )}

            {!loading && !historyRow && (
              <div className="flex items-center justify-center py-8">
                <p className="text-sm text-gray-400">
                  Select a history entry to view its form submission detail.
                </p>
              </div>
            )}

            {historyRow && ktpPhotoUrl && (
              <MyDoubleCard heading="Identity" innerClassName="p-0">
                <div className="overflow-hidden">
                  {/* /uploads is auth-gated — a plain <img> can't send the
                      Bearer and 401s (broken image). AuthImage fetches the bytes
                      with auth and renders a blob (Everest #z94x8e1hkj). */}
                  <AuthImage
                    src={ktpPhotoUrl}
                    alt="ID card (KTP)"
                    className="h-auto w-full object-cover"
                  />
                </div>
              </MyDoubleCard>
            )}

            {historyRow && selfiePhotoUrl && (
              <MyDoubleCard heading="Compared Face" innerClassName="p-0">
                <div className="overflow-hidden">
                  <AuthImage
                    src={selfiePhotoUrl}
                    alt="Selfie used for face comparison"
                    className="aspect-square h-auto w-full object-cover"
                  />
                </div>
              </MyDoubleCard>
            )}

            {historyRow && Number.isFinite(score) && (
              <div className="flex w-full items-center gap-4 rounded-xl bg-brand/25 p-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand/600 text-[10px] font-semibold text-white">
                  {score}%
                </div>
                <div className="flex flex-1 flex-col gap-2">
                  <span className="text-sm font-medium text-brand/900">
                    Face Match Score
                  </span>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-white">
                    <div
                      className="h-full rounded-full bg-brand/600 transition-all duration-500"
                      style={{ width: `${score}%` }}
                      role="progressbar"
                      aria-valuenow={score}
                      aria-valuemin="0"
                      aria-valuemax="100"
                    />
                  </div>
                </div>
              </div>
            )}

            {historyRow && identityFields && (
              <MyDoubleCard heading="Identity" innerClassName="p-4">
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-1.5">
                    <div className="text-sm font-medium text-gray-700">NIK</div>
                    <MyTextField readOnly value={identityFields.nik} />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <div className="text-sm font-medium text-gray-700">Full Name</div>
                    <MyTextField readOnly value={identityFields.fullName} />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <div className="text-sm font-medium text-gray-700">Date of Birth</div>
                    <MyTextField readOnly value={identityFields.dob} />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <div className="text-sm font-medium text-gray-700">Gender</div>
                    <MyTextField readOnly value={identityFields.gender} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1.5">
                      <div className="text-sm font-medium text-gray-700">City</div>
                      <MyTextField readOnly value={identityFields.city} />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <div className="text-sm font-medium text-gray-700">District</div>
                      <MyTextField readOnly value={identityFields.district} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1.5">
                      <div className="text-sm font-medium text-gray-700">Subdistrict</div>
                      <MyTextField readOnly value={identityFields.subdistrict} />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <div className="text-sm font-medium text-gray-700">Postal Code</div>
                      <MyTextField readOnly value={identityFields.postalCode} />
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <div className="text-sm font-medium text-gray-700">
                      Address (as stated on ID)
                    </div>
                    <MyTextArea readOnly value={identityFields.address} />
                  </div>
                </div>
              </MyDoubleCard>
            )}

            {historyRow && whatsappNumber && whatsappNumber !== '-' && (
              <MyDoubleCard heading="WhatsApp" innerClassName="p-0">
                <div className="flex items-center gap-2 p-4 px-5">
                  <WhatsApp className="size-4 text-green-500" />
                  <span className="text-sm font-semibold text-gray-900">
                    {whatsappNumber}
                  </span>
                </div>
              </MyDoubleCard>
            )}

            {historyRow && row.actor_name && (
              <MyDoubleCard heading="Changes" innerClassName="p-4">
                {/* min-w-0 + overflow-wrap: a long actor name wraps inside the
                    card instead of running past its edge (same as Settings). */}
                <div className="flex min-w-0 items-start justify-between gap-3">
                  <span className="shrink-0 text-sm font-medium text-gray-700">
                    {isRejection ? 'Rejected' : statusLabel}
                  </span>
                  <div className="flex min-w-0 items-start justify-end gap-3">
                    <div className="flex min-w-0 flex-col items-end gap-0.5 text-right">
                      <span className="max-w-full text-sm font-semibold text-gray-900 [overflow-wrap:anywhere]">
                        {row.actor_name}
                      </span>
                      <span className="text-xs text-gray-500">
                        {ACTOR_TYPE_LABEL[row.actor_type] || row.actor_type || ''}
                      </span>
                      <span className="text-xs text-gray-500 whitespace-nowrap">
                        {formatDateTime(row.date)}
                      </span>
                    </div>
                    <div className="shrink-0">
                      <MyAvatar photo={row.actor_avatar_url} size={32} />
                    </div>
                  </div>
                </div>
              </MyDoubleCard>
            )}
          </div>
        </SimpleBar>
      </section>
    </div>
  )
}
